import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, AlertTriangle, DollarSign, Clock, CheckCircle, XCircle,
  ChevronDown, ChevronRight, Activity, Wallet, FileText, Lock,
  Zap, TrendingUp, ArrowDownToLine, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function safeFetch(url, opts = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method || 'GET', url);
    if (opts.headers) Object.entries(opts.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => { try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({ error: xhr.responseText }); } };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(opts.body || null);
  });
}

const LEVEL_COLORS = {
  healthy: 'emerald', repaid: 'emerald', no_debt: 'emerald',
  adequate: 'blue',
  warning: 'amber', repayment_pending: 'amber',
  critical: 'red', overdue: 'red', grace_period: 'red',
  liquidation_eligible: 'red', defaulted: 'red', undercollateralized: 'red',
};

function Badge({ level, children }) {
  const c = LEVEL_COLORS[level] || 'gray';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${c}-500/15 text-${c}-400 border border-${c}-500/20`} data-testid={`badge-${level}`}>
      {children || level}
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon, color = 'gray' }) {
  return (
    <div className={`bg-white/[0.02] border border-white/5 rounded-xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 text-${color}-400`} />}
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-lg font-bold text-white">{value}</div>
      {sub && <div className="text-xs text-gray-600 mt-1">{sub}</div>}
    </div>
  );
}

/* ── Collateral Health Panel ── */
function CollateralHealth({ agentAddress }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHealth = useCallback(async () => {
    if (!agentAddress) return;
    setLoading(true);
    try {
      const data = await safeFetch(`${API}/api/agent-credit/repayment/agent/${agentAddress}/collateral-health`);
      if (!data.detail) setHealth(data);
    } catch {}
    setLoading(false);
  }, [agentAddress]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  if (!health) return null;

  const ratioDisplay = health.collateral_ratio === 'infinite' ? 'No Debt' : `${(health.collateral_ratio * 100).toFixed(0)}%`;
  const c = LEVEL_COLORS[health.health] || 'gray';

  return (
    <div className={`bg-${c}-500/[0.03] border border-${c}-500/20 rounded-xl p-5 mb-6`} data-testid="collateral-health">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className={`w-5 h-5 text-${c}-400`} />
          <h3 className="text-sm font-bold text-white">Collateral Health</h3>
          <Badge level={health.health}>{health.health.replace('_', ' ')}</Badge>
        </div>
        <button onClick={fetchHealth} className="text-gray-500 hover:text-white transition-colors" data-testid="refresh-health">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCard label="Collateral Ratio" value={ratioDisplay} sub={`Min: ${(health.min_required_ratio * 100)}%`} icon={Shield} color={c} />
        <StatCard label="Staked USDC Collateral" value={health.staked_kutro?.toLocaleString()} sub={`$${health.collateral_value_usd?.toFixed(2)} USD`} icon={Lock} color="blue" />
        <StatCard label="Outstanding Debt" value={`$${health.total_outstanding_debt?.toFixed(2)}`} sub={`${health.total_escrows} escrows`} icon={DollarSign} color="amber" />
        <StatCard label="Risk Level" value={health.risk_level} icon={AlertTriangle} color={c} />
      </div>

      <p className={`text-xs text-${c}-400/80`}>{health.recommendation}</p>
    </div>
  );
}

/* ── Repayment Status for single escrow ── */
function EscrowRepaymentCard({ escrowId, onRepaid }) {
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [repayAmount, setRepayAmount] = useState('');
  const [paying, setPaying] = useState(false);
  const [payResult, setPayResult] = useState(null);

  useEffect(() => {
    safeFetch(`${API}/api/agent-credit/repayment/escrow/${escrowId}/status`)
      .then(d => { if (!d.detail) setStatus(d); });
  }, [escrowId]);

  if (!status) return null;

  const esc = status.escalation || {};
  const c = LEVEL_COLORS[esc.level] || 'gray';
  const progressPct = Math.min(status.repayment_progress_pct || 0, 100);

  const handleRepay = async () => {
    const amt = parseFloat(repayAmount);
    if (!amt || amt <= 0) return;
    setPaying(true);
    setPayResult(null);
    try {
      const res = await safeFetch(`${API}/api/agent-credit/repayment/escrow/${escrowId}/repay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_address: status.buyer_address, amount: amt, payment_source: 'agent_wallet' }),
      });
      setPayResult(res);
      if (res.is_fully_repaid && onRepaid) onRepaid();
      // Refresh status
      const updated = await safeFetch(`${API}/api/agent-credit/repayment/escrow/${escrowId}/status`);
      if (!updated.detail) setStatus(updated);
      setRepayAmount('');
    } catch (err) {
      setPayResult({ error: err.message });
    }
    setPaying(false);
  };

  return (
    <div className={`border border-white/5 rounded-xl overflow-hidden mb-3 ${status.status === 'repaid' ? 'opacity-70' : ''}`} data-testid={`repayment-card-${escrowId}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
      >
        <div className="flex items-center gap-3">
          <Badge level={esc.level}>{esc.level?.replace('_', ' ')}</Badge>
          <span className="text-sm font-mono text-gray-300">{escrowId.slice(0, 8)}...</span>
          <span className="text-sm text-gray-500">${status.principal?.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-32 bg-white/5 rounded-full h-2 overflow-hidden">
            <div className={`h-full bg-${c}-400 rounded-full transition-all`} style={{ width: `${progressPct}%` }} />
          </div>
          <span className="text-xs text-gray-500 w-12 text-right">{progressPct.toFixed(0)}%</span>
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
        </div>
      </button>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-4 border-t border-white/5 space-y-4">
              {/* Numbers */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <span className="text-xs text-gray-600">Principal</span>
                  <div className="text-sm font-medium text-white">${status.principal?.toFixed(2)}</div>
                </div>
                <div>
                  <span className="text-xs text-gray-600">Interest</span>
                  <div className="text-sm font-medium text-amber-400">${status.interest?.total_interest?.toFixed(4)}</div>
                </div>
                <div>
                  <span className="text-xs text-gray-600">Late Fee</span>
                  <div className="text-sm font-medium text-red-400">${status.late_fee?.toFixed(4)}</div>
                </div>
                <div>
                  <span className="text-xs text-gray-600">Total Owed</span>
                  <div className="text-sm font-bold text-white">${status.total_owed?.toFixed(4)}</div>
                </div>
              </div>

              {/* Repayment Progress */}
              <div className="bg-white/[0.02] rounded-lg p-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Repaid: ${status.total_repaid?.toFixed(2)}</span>
                  <span>Remaining: ${status.remaining_balance?.toFixed(4)}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-3 overflow-hidden">
                  <div className={`h-full bg-${c}-400 rounded-full transition-all`} style={{ width: `${progressPct}%` }} />
                </div>
              </div>

              {/* Escalation Info */}
              {esc.message && (
                <div className={`flex items-center gap-2 text-xs text-${c}-400 bg-${c}-500/[0.05] rounded-lg px-3 py-2`}>
                  {esc.level === 'healthy' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  {esc.message}
                </div>
              )}

              {/* Payment Actions (only if not fully repaid) */}
              {status.status !== 'repaid' && (
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={repayAmount}
                    onChange={e => setRepayAmount(e.target.value)}
                    placeholder="Amount to repay"
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                    data-testid={`repay-input-${escrowId}`}
                  />
                  <button
                    onClick={() => setRepayAmount(status.remaining_balance?.toFixed(2))}
                    className="px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:border-white/20 transition-all"
                  >
                    Max
                  </button>
                  <button
                    onClick={handleRepay}
                    disabled={paying || !repayAmount}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-sm font-medium text-emerald-400 hover:bg-emerald-500/30 transition-all disabled:opacity-50"
                    data-testid={`repay-btn-${escrowId}`}
                  >
                    {paying ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowDownToLine className="w-3.5 h-3.5" />}
                    Repay
                  </button>
                </div>
              )}

              {/* Payment Result */}
              {payResult && (
                <div className={`text-xs px-3 py-2 rounded-lg ${payResult.error || payResult.detail ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  {payResult.message || payResult.detail || payResult.error}
                  {payResult.proof_hash && (
                    <div className="text-gray-600 mt-1 font-mono">Proof: {payResult.proof_hash.slice(0, 20)}...</div>
                  )}
                </div>
              )}

              {/* Payment History */}
              {status.payments?.length > 0 && (
                <div>
                  <div className="text-xs text-gray-600 mb-2">Payment History ({status.payments.length})</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {status.payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-white/[0.02] rounded px-3 py-1.5">
                        <span className="text-gray-400">{new Date(p.timestamp).toLocaleString()}</span>
                        <span className="text-gray-500">{p.payment_source}</span>
                        <span className="text-emerald-400 font-mono">${p.amount?.toFixed(2)}</span>
                        {p.is_final_payment && <CheckCircle className="w-3 h-3 text-emerald-400" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main Repayment Dashboard ── */
export default function RepaymentDashboard() {
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [agentAddress, setAgentAddress] = useState('');
  const [lookupAddress, setLookupAddress] = useState('');
  const [liquidationResult, setLiquidationResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    safeFetch(`${API}/api/agent-credit/repayment/stats`).then(d => { if (!d.detail) setStats(d); });
  }, []);

  const loadSchedule = async () => {
    if (!agentAddress) return;
    setLookupAddress(agentAddress);
    const data = await safeFetch(`${API}/api/agent-credit/repayment/agent/${agentAddress}/schedule`);
    if (!data.detail) setSchedule(data);
  };

  const runLiquidation = async () => {
    setRunning(true);
    const res = await safeFetch(`${API}/api/agent-credit/repayment/liquidation/run`, { method: 'POST' });
    setLiquidationResult(res);
    setRunning(false);
    // Refresh stats
    safeFetch(`${API}/api/agent-credit/repayment/stats`).then(d => { if (!d.detail) setStats(d); });
  };

  return (
    <div data-testid="repayment-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" />
            Repayment Engine
          </h2>
          <p className="text-sm text-gray-500 mt-1">Iron-clad credit settlement with cryptographic proofs</p>
        </div>
        <button
          onClick={runLiquidation}
          disabled={running}
          className="flex items-center gap-1.5 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400 hover:bg-red-500/20 transition-all disabled:opacity-50"
          data-testid="run-liquidation-btn"
        >
          {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
          Run Liquidation Check
        </button>
      </div>

      {/* Liquidation Result */}
      {liquidationResult && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-white/[0.02] border border-white/5 rounded-xl p-4">
          <div className="text-sm text-white font-medium mb-2">{liquidationResult.message}</div>
          {liquidationResult.actions?.length > 0 && (
            <div className="space-y-1">
              {liquidationResult.actions.map((a, i) => (
                <div key={i} className="text-xs text-gray-400 flex items-center gap-2">
                  <Badge level={a.action === 'liquidated' ? 'defaulted' : a.action === 'credit_frozen' ? 'critical' : 'warning'}>
                    {a.action}
                  </Badge>
                  <span className="font-mono">{a.escrow_id?.slice(0, 8)}...</span>
                  {a.kutro_slashed && <span className="text-red-400">{a.kutro_slashed.toLocaleString()} KOJIRU slashed</span>}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Auto-Liquidation Cron Status */}
      {stats?.auto_liquidation_cron && (
        <div className="flex items-center justify-between mb-4 bg-white/[0.02] border border-white/5 rounded-lg px-4 py-2.5">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${stats.auto_liquidation_cron.status === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
            <span className="text-xs text-gray-400">Auto-Liquidation Cron</span>
            <span className="text-xs text-gray-600">every {stats.auto_liquidation_cron.interval}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {stats.auto_liquidation_cron.last_run ? (
              <>
                <span>Last: <span className="text-gray-400">{new Date(stats.auto_liquidation_cron.last_run).toLocaleString()}</span></span>
                <span>Scanned: <span className="text-gray-400">{stats.auto_liquidation_cron.escrows_scanned}</span></span>
                {stats.auto_liquidation_cron.liquidations > 0 && (
                  <span className="text-red-400">{stats.auto_liquidation_cron.liquidations} liquidations</span>
                )}
              </>
            ) : (
              <span className="text-amber-400">{stats.auto_liquidation_cron.message}</span>
            )}
          </div>
        </div>
      )}

      {/* Global Stats */}
      {stats && (
        <div className="grid grid-cols-5 gap-3 mb-6">
          <StatCard label="Total Repaid" value={`$${stats.repayments.total_volume.toLocaleString()}`} sub={`${stats.repayments.count} payments`} icon={DollarSign} color="emerald" />
          <StatCard label="Revenue Assigned" value={`$${stats.revenue_assignments.total_volume.toLocaleString()}`} sub={`${stats.revenue_assignments.count} assignments`} icon={TrendingUp} color="blue" />
          <StatCard label="Defaults" value={stats.defaults.count} sub={`$${stats.defaults.total_debt.toLocaleString()} debt`} icon={XCircle} color="red" />
          <StatCard label="KOJIRU Slashed" value={stats.defaults.kutro_slashed.toLocaleString()} sub={`$${stats.defaults.recovered_usd.toLocaleString()} recovered`} icon={Lock} color="amber" />
          <StatCard label="Proofs Generated" value={stats.proofs_generated} sub="Cryptographic audit trail" icon={FileText} color="purple" />
        </div>
      )}

      {/* Protocol Constants */}
      {stats?.constants && (
        <div className="flex items-center gap-4 mb-6 text-xs text-gray-600 bg-white/[0.02] border border-white/5 rounded-lg px-4 py-2.5">
          <span>Grace: <span className="text-gray-400">{stats.constants.grace_period_days}d</span></span>
          <span className="text-white/10">|</span>
          <span>Min Collateral: <span className="text-gray-400">{stats.constants.min_collateral_ratio}</span></span>
          <span className="text-white/10">|</span>
          <span>Liquidation: <span className="text-gray-400">{stats.constants.liquidation_ratio}</span></span>
          <span className="text-white/10">|</span>
          <span>Late Fee: <span className="text-gray-400">{stats.constants.late_fee_daily}/day</span></span>
          <span className="text-white/10">|</span>
          <span>Slash: <span className="text-gray-400">{stats.constants.slash_ratio}</span></span>
        </div>
      )}

      {/* Agent Lookup */}
      <div className="mb-6">
        <div className="flex gap-3">
          <input
            value={agentAddress}
            onChange={e => setAgentAddress(e.target.value)}
            placeholder="Enter agent address (0x...) to view repayment schedule"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:border-emerald-500/50 placeholder:text-gray-700"
            data-testid="repayment-agent-input"
          />
          <button
            onClick={loadSchedule}
            disabled={!agentAddress}
            className="px-5 py-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-sm font-medium text-emerald-400 hover:bg-emerald-500/30 transition-all disabled:opacity-50"
            data-testid="repayment-lookup-btn"
          >
            Lookup
          </button>
        </div>
      </div>

      {/* Collateral Health + Schedule */}
      {lookupAddress && <CollateralHealth agentAddress={lookupAddress} />}

      {schedule && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              Repayment Schedule
              <span className="text-gray-600 font-normal">({schedule.total_escrows} escrows)</span>
            </h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="text-gray-500">Total Owed: <span className="text-white font-medium">${schedule.total_owed?.toFixed(2)}</span></span>
              <span className="text-gray-500">Repaid: <span className="text-emerald-400 font-medium">${schedule.total_repaid?.toFixed(2)}</span></span>
              <span className="text-gray-500">Remaining: <span className="text-amber-400 font-medium">${schedule.total_remaining?.toFixed(2)}</span></span>
            </div>
          </div>

          {schedule.schedule?.length > 0 ? (
            schedule.schedule.map(esc => (
              <EscrowRepaymentCard key={esc.escrow_id} escrowId={esc.escrow_id} onRepaid={loadSchedule} />
            ))
          ) : (
            <div className="text-center text-sm text-gray-600 py-8 bg-white/[0.02] border border-white/5 rounded-xl">
              No repayable escrows found for this agent.
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!schedule && !lookupAddress && (
        <div className="text-center py-12 bg-white/[0.02] border border-white/5 border-dashed rounded-xl">
          <Shield className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Enter an agent address above to view its repayment schedule and collateral health</p>
          <p className="text-xs text-gray-700 mt-1">Every payment generates a cryptographic proof for full audit trail</p>
        </div>
      )}
    </div>
  );
}
